/**
 * The part every game on this platform shares: a grid, and a run of marks on it.
 *
 * OX, Connect Four and Gomoku differ in the size of the board, how many in a
 * row wins, and where a piece is allowed to land. They do **not** differ in how
 * a win is found — all three are "N of the same mark in a line" — so that is
 * written once here rather than three times with three chances to miss a
 * direction.
 *
 * A board is a single string, left to right and top to bottom, because that is
 * also how it is stored: one column in MySQL, compared and written whole.
 */

export type Mark = 'X' | 'O';

export const EMPTY_CELL = '.';

/** The four directions a run can go. The opposite four are the same lines read backwards. */
const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [1, 0], // →
  [0, 1], // ↓
  [1, 1], // ↘
  [1, -1], // ↗
];

export interface Grid {
  width: number;
  height: number;
  /** How many in a row wins. */
  need: number;
}

export function emptyBoard(grid: Grid): string {
  return EMPTY_CELL.repeat(grid.width * grid.height);
}

export function isBoard(board: string, grid: Grid): boolean {
  return board.length === grid.width * grid.height && /^[.XO]*$/.test(board);
}

/** Row and column of a cell, and the inverse. */
export const rowOf = (cell: number, grid: Grid): number => Math.floor(cell / grid.width);
export const columnOf = (cell: number, grid: Grid): number => cell % grid.width;
export const cellAt = (column: number, row: number, grid: Grid): number => row * grid.width + column;

/**
 * The winning run, as the cells that make it up, or null.
 *
 * Returns the cells rather than just the mark so the interface can draw through
 * them — the same reason OX returned its line before this was generalised.
 *
 * Scans from every cell in four directions. For the largest board here that is
 * 81 cells by 4, which is nothing next to the round trip that delivered the
 * move.
 */
export function winningRun(board: string, grid: Grid): number[] | null {
  for (let row = 0; row < grid.height; row += 1) {
    for (let column = 0; column < grid.width; column += 1) {
      const start = cellAt(column, row, grid);
      const mark = board[start];
      if (!mark || mark === EMPTY_CELL) continue;

      for (const [dx, dy] of DIRECTIONS) {
        // The run has to fit on the board before it is worth walking.
        const endColumn = column + dx * (grid.need - 1);
        const endRow = row + dy * (grid.need - 1);
        if (endColumn < 0 || endColumn >= grid.width) continue;
        if (endRow < 0 || endRow >= grid.height) continue;

        const run: number[] = [start];
        for (let step = 1; step < grid.need; step += 1) {
          const cell = cellAt(column + dx * step, row + dy * step, grid);
          if (board[cell] !== mark) break;
          run.push(cell);
        }
        // Ascending, always. The cells are a *set* to highlight and nothing
        // reads them in play order, but the scan walks the anti-diagonal from
        // the bottom-left — so without this, OX reported its anti-diagonal as
        // [6,4,2] where it had always been [2,4,6], and anything comparing the
        // array rather than its members saw a different answer to the same
        // board.
        if (run.length === grid.need) return run.sort((a, b) => a - b);
      }
    }
  }
  return null;
}

export function winnerOf(board: string, grid: Grid): Mark | null {
  const run = winningRun(board, grid);
  if (!run) return null;
  const mark = board[run[0]!];
  return mark === 'X' || mark === 'O' ? mark : null;
}

/**
 * Whose turn it is, counted from the board rather than stored.
 *
 * X moves first, so an even number of marks means X is to play. Derived for the
 * same reason it always was: a stored turn is a second answer to a question the
 * board already answers, and the two drift the first time a write half-fails.
 *
 * This holds for all three games here because none of them can pass. A game
 * that could — Reversi, where a player with no legal move must — would need its
 * own answer, and that is a reason to think before adding one rather than a
 * flaw in this.
 */
export function turnOf(board: string): Mark {
  let placed = 0;
  for (const cell of board) if (cell !== EMPTY_CELL) placed += 1;
  return placed % 2 === 0 ? 'X' : 'O';
}

/**
 * A full board with no winner.
 *
 * Checked **after** the winner, never instead: the last move can win, and a
 * draw declared on fullness alone would take the game off whoever just won it.
 */
export function isDraw(board: string, grid: Grid): boolean {
  if (winnerOf(board, grid) !== null) return false;
  return !board.includes(EMPTY_CELL);
}

export function isOver(board: string, grid: Grid): boolean {
  return winnerOf(board, grid) !== null || isDraw(board, grid);
}

/** Places a mark, with no rules attached beyond the bounds of the string. */
export function withMark(board: string, cell: number, mark: Mark): string {
  return board.slice(0, cell) + mark + board.slice(cell + 1);
}
